import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { DataAddType } from 'src/common/enumerations/data-add-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { TenderType } from 'src/common/enumerations/tender-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { getFormattedLongDate, isDateBeforeToday } from 'src/common/helpers/date-helper';
import {
  TenderAmountExceededEligibilityAmount,
  TenderChangeAvailability,
  TenderExchangeInvalidTender,
} from 'src/common/helpers/error-code';
import { PaymentUsage } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { store } from 'src/common/redux/configure-store';
import { cancelTenderPayment } from 'src/home-page/business-logic/cancel-tender-payment';
import { getDataInformation } from 'src/home-page/business-logic/get-data-infomation';
import { getPaymentOptions } from 'src/home-page/business-logic/get-payment-options';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { addTender } from 'src/tendering/business-logic/handle-tender-payment';
import { IDataInformation } from '../../common/interfaces/dtos/IDataInformation';

export const voucherPayment = async (barCode: string, usage?: PaymentUsage): Promise<number> => {
  const dataInfo = await getDataInformation({ itemId: barCode });
  await performBarcodeTenderAdd(dataInfo, usage);
  return dataInfo.amount ?? 0; // amount won't be 0 and validated in barcodeTenderAdd
};

export const performBarcodeTenderAdd = async (
  dataInfo: IDataInformation,
  usage?: PaymentUsage,
): Promise<void> => {
  if (!dataInfo.tenderId) {
    throw new EmeraldError('InvalidVoucher');
  }
  if (!dataInfo.amount) {
    throw new EmeraldError('InvalidVoucherAmount');
  }
  if (usage !== PaymentUsage.ExchangePay) {
    // TODO this logic should be moved to add first transaction line, check data type before start transaction
    if (store.getState().itemList.ejItems.length === 0) {
      throw noTransactionVoucherError;
    }
  }
  validateExpirationDate(dataInfo);
  await validateVoucherTender(dataInfo);

  const amountDue = usage === PaymentUsage.ExchangePay ? 0 : store.getState().itemList.amountDue;

  const tender = {
    tender: {
      id: dataInfo.tenderId,
      amount: dataInfo.amount,
      type: TenderType.Voucher,
      ...(dataInfo.expirationDate && { expirationDate: dataInfo.expirationDate }),
    },
    amount: dataInfo.amount,
    amountDue,
    lineItemEntryMethod: EntryMethod.Scanned,
    usage,
  };

  return addTender(tender).catch(onVoucherBarcodeError);
};

export const onVoucherBarcodeError = async (err: EmeraldError): Promise<void> => {
  if (err.code == TenderExchangeInvalidTender) {
    throw new EmeraldErrorModal({
      message: 'InvalidTender',
      subMessage: 'InvalidTender_SubHeader',
      type: EmeraldDialogTypes.Error,
      acceptButtonLabel: 'Close',
    });
  } else if (err.code == TenderChangeAvailability) {
    await cancelTenderPayment(err.data.lineId).catch(popupMessage);
    throw new EmeraldErrorModal({
      message: 'TenderNotAllowedForChangeHeading',
      subMessage: 'TenderNotAllowedForChange',
      type: EmeraldDialogTypes.Error,
      acceptButtonLabel: 'Ok',
    });
  } else if (err.code === TenderAmountExceededEligibilityAmount) {
    const maxAmount = err.data.eligibility.maxEligibleAmount;
    const eligibilityAmount = convertCurrency(maxAmount);
    throw new EmeraldErrorModal({
      message: 'Tender_Restriction',
      subMessage: 'Tender_Eligibility_Amount',
      type: EmeraldDialogTypes.Info,
      subMessageArgs: { arg: eligibilityAmount },
    });
  } else {
    throw err;
  }
};

const noTransactionVoucherError = new EmeraldErrorModal({
  message: 'VoucherNoTransactionMessage',
  subMessage: 'VoucherNoTransactionSubMessage',
  type: EmeraldDialogTypes.Error,
  disableBackdropClick: false,
});

export const validateVoucherTender = async (dataInformation: IDataInformation): Promise<void> => {
  if (dataInformation.type === DataAddType.Tender) {
    if (store.getState().tenderMenu.sellableTenders.rawTenders.length === 0) {
      await getPaymentOptions();
    }
    const tender = store
      .getState()
      .tenderMenu.sellableTenders.rawTenders.find(t => t.id === dataInformation.tenderId);
    if (tender?.type === TenderType.Voucher) {
      return;
    }
  }
  throw new EmeraldError('InvalidVoucher');
};

export const validateExpirationDate = (dataInformation: IDataInformation) => {
  if (dataInformation.expirationDate) {
    const newDate = new Date(dataInformation.expirationDate);
    const formattedDate = getFormattedLongDate(newDate.toDateString());
    const tenderExpired: boolean = isDateBeforeToday(formattedDate);

    if (tenderExpired) {
      throw new EmeraldError('VoucherExpired');
    }
  }
};
